import Swal from 'sweetalert2'

const GREEN = '#039540'
const RED = '#d9214e'
const GRAY = '#9aa1b1'

/**
 * Branded confirm dialog (SweetAlert2). Use instead of window.confirm().
 * Returns true if the user confirmed.
 */
export async function confirmDialog(opts: {
  title: string
  text?: string
  confirmText?: string
  cancelText?: string
  danger?: boolean
}): Promise<boolean> {
  const res = await Swal.fire({
    title: opts.title,
    text: opts.text,
    icon: opts.danger ? 'warning' : 'question',
    showCancelButton: true,
    confirmButtonText: opts.confirmText ?? 'Confirm',
    cancelButtonText: opts.cancelText ?? 'Cancel',
    confirmButtonColor: opts.danger ? RED : GREEN,
    cancelButtonColor: GRAY,
    reverseButtons: true,
    buttonsStyling: true,
  })
  return res.isConfirmed
}

/**
 * Three-way choice dialog (confirm / deny / cancel). Returns which the user
 * picked. Use when an action has two positive options plus cancel.
 */
export async function choiceDialog(opts: {
  title: string
  text?: string
  confirmText: string
  denyText: string
  cancelText?: string
}): Promise<'confirm' | 'deny' | 'cancel'> {
  const res = await Swal.fire({
    title: opts.title,
    text: opts.text,
    icon: 'question',
    showDenyButton: true,
    showCancelButton: true,
    confirmButtonText: opts.confirmText,
    denyButtonText: opts.denyText,
    cancelButtonText: opts.cancelText ?? 'Cancel',
    confirmButtonColor: GREEN,
    denyButtonColor: '#2b4eff',
    cancelButtonColor: GRAY,
    buttonsStyling: true,
  })
  return res.isConfirmed ? 'confirm' : res.isDenied ? 'deny' : 'cancel'
}

/**
 * Branded text-input dialog (SweetAlert2). Use instead of window.prompt().
 * Returns the entered text, or null if cancelled.
 */
export async function promptDialog(opts: {
  title: string
  text?: string
  inputLabel?: string
  inputPlaceholder?: string
  confirmText?: string
  required?: boolean
}): Promise<string | null> {
  const res = await Swal.fire({
    title: opts.title,
    text: opts.text,
    input: 'textarea',
    inputLabel: opts.inputLabel,
    inputPlaceholder: opts.inputPlaceholder,
    showCancelButton: true,
    confirmButtonText: opts.confirmText ?? 'Submit',
    cancelButtonText: 'Cancel',
    confirmButtonColor: GREEN,
    cancelButtonColor: GRAY,
    reverseButtons: true,
    buttonsStyling: true,
    inputValidator: opts.required
      ? (v) => (v && v.trim() ? undefined : 'This field is required')
      : undefined,
  })
  if (!res.isConfirmed) return null
  return (res.value as string) ?? ''
}
